import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api, { formatApiError } from '../api/client';
import Modal from '../components/Modal';
import { CheckCircle2, XCircle, UserCheck, RefreshCw, AlertTriangle, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';

// Web port of the mobile Visitor Requests screen (mobile/app/(tabs)/visitor-requests.js),
// which is the source of truth for behaviour. Intentional web-only differences:
//   - real backend error messages (mobile shows a generic one)
//   - expired, unused passes are labelled "Expired" with the QR hidden
//   - a load failure shows an error state with Retry instead of an empty list
//   - refresh on tab focus + Refresh button (mobile: on screen focus + pull-to-refresh)
//   - pass image is shared via the browser share sheet where supported, else downloaded

function fmtDT(iso) {
  if (!iso) return '—';
  return format(new Date(iso), 'd MMM yyyy, h:mm a');
}

const errorText = (e) => formatApiError(e?.response?.data?.detail, e?.response?.data);

// Same colour coding as mobile. Mobile's `expired` entry is omitted: the backend never
// sets a request's status to expired (only the gate pass expires — handled below).
const STATUS = {
  pending:  { badge: 'badge-warn',   label: 'Pending',  border: 'border-l-yellow-400' },
  approved: { badge: 'badge-info',   label: 'Approved', border: 'border-l-blue-400'   },
  rejected: { badge: 'badge-danger', label: 'Rejected', border: 'border-l-red-400'    },
};

const FILTER_TABS = [
  ['pending', 'Pending'],
  ['approved', 'Approved'],
  ['rejected', 'Rejected'],
  ['all', 'All'],
];

// A pass can no longer be used at the gate if it expired, or lapsed while still unused.
// A checked-in visitor still needs the QR to check out, so it is never "expired" for them.
function isPassExpired(item) {
  const vgp = item.visitor_gate_pass;
  if (!vgp || item.status === 'checked_in') return false;
  return vgp.status === 'expired' || (vgp.status === 'active' && new Date() > new Date(vgp.valid_until));
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="overline w-24 flex-shrink-0 leading-relaxed">{label}</span>
      <span className="text-zinc-700 flex-1">{value}</span>
    </div>
  );
}

// ── Pass image (mobile "Share" equivalent) ─────────────────────────────────────

const canShareFiles = () => {
  try {
    return typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [new File([''], 'x.png', { type: 'image/png' })] });
  } catch {
    return false;
  }
};

// Same layout as the mobile pass card: name, phone, QR, code, validity rows, footer.
function renderPassImage(vgp, qrCanvas) {
  const W = 600, H = 800;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);
  g.textAlign = 'center';
  g.fillStyle = '#09090B'; g.font = '900 32px system-ui, sans-serif';
  g.fillText(vgp.visitor_name || '', W / 2, 70);
  g.fillStyle = '#71717A'; g.font = '22px system-ui, sans-serif';
  g.fillText(vgp.visitor_phone || '', W / 2, 106);
  const qr = 340, qx = (W - qr) / 2, qy = 150;
  g.strokeStyle = '#09090B'; g.lineWidth = 4;
  g.strokeRect(qx - 22, qy - 22, qr + 44, qr + 44);
  g.drawImage(qrCanvas, qx, qy, qr, qr);
  g.fillStyle = '#71717A'; g.font = '22px ui-monospace, monospace';
  g.fillText(vgp.qr_code, W / 2, qy + qr + 64);
  g.textAlign = 'left';
  [['VALID FROM', fmtDT(vgp.valid_from)], ['VALID UNTIL', fmtDT(vgp.valid_until)]].forEach(([label, value], i) => {
    const y = qy + qr + 122 + i * 38;
    g.fillStyle = '#71717A'; g.font = '700 16px system-ui, sans-serif'; g.fillText(label, 80, y);
    g.fillStyle = '#09090B'; g.font = '20px system-ui, sans-serif'; g.fillText(value, 230, y);
  });
  g.textAlign = 'center';
  g.fillStyle = '#71717A'; g.font = '18px system-ui, sans-serif';
  g.fillText('Show this QR at the gate for entry.', W / 2, H - 40);
  return c;
}

function PassModal({ vgp, onClose }) {
  const qrRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'share' | 'download' | null
  // The share sheet only exists in some browsers (mostly phones); Download is always offered.
  const share = canShareFiles();

  const passFile = async () => {
    const qrCanvas = qrRef.current?.querySelector('canvas');
    if (!qrCanvas) throw new Error('QR not ready');
    const blob = await new Promise((r) => renderPassImage(vgp, qrCanvas).toBlob(r, 'image/png'));
    return new File([blob], `visitor-pass-${vgp.qr_code}.png`, { type: 'image/png' });
  };

  const sharePass = async () => {
    setBusy('share');
    try {
      await navigator.share({ files: [await passFile()], title: 'Visitor Pass' });
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Could not share the pass image.');
    } finally {
      setBusy(null);
    }
  };

  const downloadPass = async () => {
    setBusy('download');
    try {
      const file = await passFile();
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url; a.download = file.name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the pass image.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Visitor Gate Pass"
      footer={
        <>
          {share && (
            <button className="btn-outline" onClick={sharePass} disabled={!!busy} data-testid="share-pass">
              <Share2 size={14} /> {busy === 'share' ? 'Sharing…' : 'Share'}
            </button>
          )}
          <button className="btn-primary" onClick={downloadPass} disabled={!!busy} data-testid="download-pass">
            <Download size={14} /> {busy === 'download' ? 'Preparing…' : 'Download'}
          </button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="text-center">
          <div className="font-bold text-xl text-ink">{vgp.visitor_name}</div>
          <div className="text-zinc-500 text-sm font-mono mt-0.5">{vgp.visitor_phone}</div>
        </div>
        <div className="p-4 border-2 border-zinc-900 rounded-sm">
          <QRCodeSVG value={vgp.qr_code} size={180} level="M" includeMargin={false} />
        </div>
        <div className="font-mono text-sm text-zinc-400 tracking-wider">{vgp.qr_code}</div>
        <div className="w-full max-w-xs space-y-1">
          <Row label="Valid from" value={fmtDT(vgp.valid_from)} />
          <Row label="Valid until" value={fmtDT(vgp.valid_until)} />
        </div>
        <div className="text-sm text-zinc-500 text-center">Show this QR at the gate for entry.</div>
        {/* High-resolution QR used only to build the shared/downloaded image */}
        <div ref={qrRef} className="hidden">
          <QRCodeCanvas value={vgp.qr_code} size={680} level="M" includeMargin={false} />
        </div>
      </div>
    </Modal>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function VisitorRequests() {
  const [requests,      setRequests]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [loadError,     setLoadError]     = useState('');
  const [refreshing,    setRefreshing]    = useState(false);
  const [filter,        setFilter]        = useState('pending');
  const [qrModal,       setQrModal]       = useState(null);     // VGP object
  const [rejectTarget,  setRejectTarget]  = useState(null);     // visitor request
  const [reason,        setReason]        = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const lastLoadRef = useRef(0);

  const load = useCallback(async () => {
    lastLoadRef.current = Date.now();
    try {
      const { data } = await api.get('/visitor-requests');
      setRequests(data);
      setLoadError('');
    } catch (e) {
      setLoadError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  // Mobile reloads whenever the screen comes into focus; the web equivalent is the tab
  // regaining focus. `focus` and `visibilitychange` often fire together — de-duplicate.
  useEffect(() => {
    load();
    const onFocus = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoadRef.current > 2000) load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return requests;
    return requests.filter((r) => r.request_status === filter);
  }, [requests, filter]);

  const pendingCount = useMemo(
    () => requests.filter((r) => r.request_status === 'pending').length,
    [requests],
  );

  const approve = async (item) => {
    setActionLoading(item.id);
    try {
      const { data } = await api.post(`/visitor-requests/${item.id}/approve`, {});
      setRequests((prev) => prev.map((r) => (r.id === item.id ? data : r)));
      toast.success(`${item.name}'s visit approved.`);
      if (data.visitor_gate_pass) setQrModal(data.visitor_gate_pass);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setActionLoading(null);
    }
  };

  // Reason is optional — same as mobile and the backend (empty → stored as no reason).
  const reject = async () => {
    if (!rejectTarget) return;
    const item = rejectTarget;
    setActionLoading(item.id);
    setRejectTarget(null);
    try {
      const { data } = await api.post(`/visitor-requests/${item.id}/reject`, { reason: reason.trim() });
      setRequests((prev) => prev.map((r) => (r.id === item.id ? data : r)));
      toast.success(`${item.name}'s request rejected.`);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setActionLoading(null);
      setReason('');
    }
  };

  return (
    <div className="space-y-6" data-testid="visitor-requests-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Approvals</div>
          <h1 className="heading text-3xl font-black text-ink">Visitor Requests</h1>
          <p className="text-sm text-zinc-500 mt-1">{pendingCount} pending</p>
        </div>
        <button className="btn-outline" onClick={refresh} disabled={refreshing || loading} data-testid="refresh-requests">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </header>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {FILTER_TABS.map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            className={filter === val ? 'btn-primary' : 'btn-outline'}
            data-testid={`vr-filter-${val}`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-12 text-zinc-400">Loading…</div>}

      {/* Load failure: say so, rather than showing an empty list */}
      {!loading && loadError && (
        <div className="card p-12 text-center" data-testid="vr-load-error">
          <AlertTriangle className="mx-auto text-red-500 mb-3" size={40} />
          <p className="text-zinc-700 font-semibold">Couldn't load visitor requests.</p>
          <p className="text-sm text-zinc-500 mt-1">{loadError}</p>
          <button className="btn-primary mt-4" onClick={refresh} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Retry
          </button>
        </div>
      )}

      {!loading && !loadError && filtered.length === 0 && (
        <div className="card p-12 text-center">
          <UserCheck className="mx-auto text-zinc-300 mb-3" size={40} />
          <p className="text-zinc-500 mt-1">
            {filter === 'pending' ? 'No pending visitor requests.' : 'No requests found.'}
          </p>
        </div>
      )}

      {!loading && !loadError && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((item) => {
            const st = STATUS[item.request_status] || { badge: 'badge-muted', label: item.request_status, border: 'border-l-zinc-300' };
            const vgp = item.visitor_gate_pass;
            const isLoading = actionLoading === item.id;
            const expired = isPassExpired(item);

            return (
              <div key={item.id} className={`card p-4 border-l-4 ${st.border}`} data-testid={`vr-card-${item.id}`}>
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-bold text-ink text-lg leading-tight">{item.name}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-0.5">{item.phone}</div>
                  </div>
                  <span className={st.badge}>{st.label}</span>
                </div>

                {/* Details — same rows and order as mobile */}
                <div className="space-y-1">
                  <Row label="Visitor Type" value={item.visitor_type} />
                  <Row label="Purpose"      value={item.purpose} />
                  <Row label="Vehicle"      value={item.vehicle_number} />
                  <Row label="Company"      value={item.company} />
                  <Row label="Requested"    value={fmtDT(item.created_at)} />
                  <Row label="By Guard"     value={item.created_by_name} />
                  {item.check_in  && <Row label="Arrived" value={fmtDT(item.check_in)} />}
                  {item.check_out && <Row label="Left"    value={fmtDT(item.check_out)} />}
                </div>

                {/* Rejection reason */}
                {item.request_status === 'rejected' && item.rejection_reason && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-sm p-2 text-xs text-red-700">
                    Reason: {item.rejection_reason}
                  </div>
                )}

                {/* Approved pass */}
                {item.request_status === 'approved' && vgp && (
                  <div className={`mt-3 rounded-sm p-3 border ${expired ? 'bg-zinc-50 border-zinc-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className={`overline ${expired ? 'text-zinc-500' : 'text-blue-700'}`}>Pass Issued</div>
                        <div className={`font-mono text-sm font-bold mt-0.5 truncate ${expired ? 'text-zinc-500 line-through' : 'text-blue-800'}`}>
                          {vgp.qr_code}
                        </div>
                        <div className={`text-xs mt-0.5 ${expired ? 'text-zinc-500' : 'text-blue-600'}`}>
                          Valid until {fmtDT(vgp.valid_until)}
                        </div>
                      </div>
                      {expired ? (
                        <span className="badge-danger flex-shrink-0" data-testid={`pass-expired-${item.id}`}>Expired</span>
                      ) : (
                        <button
                          className="btn-outline text-xs px-3 py-1.5 flex-shrink-0"
                          onClick={() => setQrModal(vgp)}
                          data-testid={`show-qr-${item.id}`}
                        >
                          Show QR
                        </button>
                      )}
                    </div>
                    {item.status === 'checked_in' && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 inline-block" />
                        <span className="text-xs text-green-700 font-semibold">Visitor is inside the premises</span>
                      </div>
                    )}
                    {item.status === 'checked_out' && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <span className="w-2 h-2 rounded-full bg-zinc-400 flex-shrink-0 inline-block" />
                        <span className="text-xs text-zinc-500 font-semibold">Visitor has left</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Pending actions */}
                {item.request_status === 'pending' && (
                  <div className="flex gap-2 mt-4">
                    <button
                      className="btn-danger flex-1 text-sm justify-center"
                      onClick={() => { setRejectTarget(item); setReason(''); }}
                      disabled={isLoading}
                      data-testid={`reject-${item.id}`}
                    >
                      <XCircle size={14} /> Reject
                    </button>
                    <button
                      className="btn-success flex-1 text-sm justify-center"
                      onClick={() => approve(item)}
                      disabled={isLoading}
                      data-testid={`approve-${item.id}`}
                    >
                      {isLoading ? '…' : <><CheckCircle2 size={14} /> Approve</>}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {qrModal && <PassModal vgp={qrModal} onClose={() => setQrModal(null)} />}

      {/* Reject dialog — reason optional, as on mobile */}
      {rejectTarget && (
        <Modal open onClose={() => setRejectTarget(null)} title="Reject Request" subtitle={rejectTarget.name}>
          <div className="space-y-4 py-2">
            <div>
              <label className="overline block mb-1.5">Reason (optional)</label>
              <textarea
                className="input w-full"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Not available today, please reschedule"
                maxLength={300}
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <button className="btn-outline flex-1" onClick={() => setRejectTarget(null)}>Cancel</button>
              <button className="btn-danger flex-1" onClick={reject} data-testid="confirm-reject">Reject</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
